import { useParams } from 'react-router-dom';
import { useStores } from 'src/hooks/use-stores';
import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_TYPES } from 'shared/constants';

import {
  GqlCustomNavigation,
  GqlCustomNavigationItem,
  GqlSaveCustomNavigationInput,
  useGetCustomNavigationQuery,
  useSaveCustomNavigationMutation,
  GetCustomNavigationDocument,
} from 'types/graphql';

type UseCustomNavigationDataReturn = {
  customNavigation: GqlCustomNavigation | null;
  error: Error | null;
  loading: boolean;
  refetch: () => void;
  saveNavigation: (
    items: GqlCustomNavigationItem[],
    label: string,
    enabled: boolean,
    targetDispensaryIds?: string[]
  ) => Promise<GqlCustomNavigation | null>;
  saving: boolean;
};

export function useCustomNavigationData(): UseCustomNavigationDataReturn {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const { apolloClient } = useStores();
  const showErnie = useErnie();

  const { data, loading, error, refetch } = useGetCustomNavigationQuery({
    client: apolloClient,
    variables: { dispensaryId },
    fetchPolicy: 'cache-and-network',
  });

  const [saveCustomNavigation, { loading: saving }] = useSaveCustomNavigationMutation({
    client: apolloClient,
    refetchQueries: [
      {
        query: GetCustomNavigationDocument,
        variables: { dispensaryId },
      },
    ],
  });

  const saveNavigation = async (
    items: GqlCustomNavigationItem[],
    label: string,
    enabled: boolean,
    targetDispensaryIds?: string[]
  ): Promise<GqlCustomNavigation | null> => {
    try {
      const input: GqlSaveCustomNavigationInput = {
        dispensaryId,
        enabled,
        items: transformItemsForSave(items),
        label,
        targetDispensaryIds,
      };

      const result = await saveCustomNavigation({
        variables: { input },
      });

      if (!result.data?.saveCustomNavigation) {
        return null;
      }

      const locationCount = targetDispensaryIds?.length ?? 1;
      const message = targetDispensaryIds
        ? `Custom navigation has been saved to ${locationCount} location${locationCount > 1 ? 's' : ''}`
        : 'Custom navigation has been saved';
      showErnie(message, ERNIE_TYPES.SUCCESS);

      // Type assertion needed: GraphQL codegen creates fixed-depth types for nested items,
      // but GqlCustomNavigation expects recursive GqlCustomNavigationItem[]. Structure is compatible at runtime.
      return result.data.saveCustomNavigation as GqlCustomNavigation;
    } catch (err) {
      showErnie('Error saving custom navigation. Please try again.', ERNIE_TYPES.DANGER);
      console.error('Error saving custom navigation:', err);
      return null;
    }
  };

  return {
    // Type assertion needed: GraphQL codegen creates fixed-depth types for nested items,
    // but GqlCustomNavigation expects recursive GqlCustomNavigationItem[]. Structure is compatible at runtime.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    customNavigation: data?.getCustomNavigation ? (data.getCustomNavigation as GqlCustomNavigation) : null,
    error: error ?? null,
    loading,
    refetch,
    saveNavigation,
    saving,
  };
}

function transformItemsForSave(items: GqlCustomNavigationItem[]): GqlSaveCustomNavigationInput['items'] {
  return items.map((item, index) => ({
    connectedId: item.connectedId,
    id: item.id,
    items: transformItemsForSave(item.items),
    label: item.label,
    openInNewTab: item.openInNewTab,
    position: index,
    navigationType: item.navigationType,
    url: item.url,
  }));
}
